import { Injectable } from '@angular/core';
import MapZoomRulesUtils from '../utils/map-zoom-rules.utils';

@Injectable()
export class MapMovementService {
  private lastCenter?: L.LatLng;
  private lastBounds?: L.LatLngBounds;
  private lastZoom?: number;

  shouldUpdate(map: L.Map, hasPolylines: boolean): boolean {
    const newZoom = map.getZoom();
    const newBounds = map.getBounds();
    const newCenter = map.getCenter();

    if (MapZoomRulesUtils.includeRouteSegments(newZoom) && !hasPolylines) {
      this.updateState(newCenter, newZoom, newBounds);
      return true;
    }

    const centerMoved = this.hasCenterMoved(newCenter);
    const boundsChanged = this.haveBoundsChanged(newBounds);
    const zoomChanged = newZoom !== this.lastZoom;

    // RULE 1: No polylines -> fetch when zoom changes or center moves beyond threshold
    if (!hasPolylines && (centerMoved || (zoomChanged && boundsChanged))) {
      this.updateState(newCenter, newZoom, newBounds);
      return true;
    }

    // RULE 2: Center moved beyond threshold → always update
    if (centerMoved) {
      this.updateState(newCenter, newZoom, newBounds);
      return true;
    }

    // RULE 3: Zoom IN + polylines present → SKIP update
    if (zoomChanged && newZoom > (this.lastZoom ?? 0) && hasPolylines) {
      return false;
    }

    // RULE 4: Zoom changed bounds beyond threshold → update
    if (zoomChanged && boundsChanged) {
      this.updateState(newCenter, newZoom, newBounds);
      return true;
    }

    return false;
  }

  getLastZoom(): number | undefined {
    return this.lastZoom;
  }

  updateState(center: L.LatLng, zoom: number, bounds: L.LatLngBounds): void {
    this.lastCenter = center;
    this.lastZoom = zoom;
    this.lastBounds = bounds;
  }

  zoomChanged(map: L.Map): boolean {
    return this.lastZoom !== map.getZoom();
  }

  private hasCenterMoved(newCenter: L.LatLng): boolean {
    if (!this.lastCenter) return true;

    const latDiff = Math.abs(newCenter.lat - this.lastCenter.lat);
    const lngDiff = Math.abs(newCenter.lng - this.lastCenter.lng);
    return latDiff > 0.1 || lngDiff > 0.2;
  }

  private haveBoundsChanged(newBounds: L.LatLngBounds): boolean {
    if (!this.lastBounds) return false;

    const newNW = newBounds.getNorthWest();
    const newSE = newBounds.getSouthEast();
    const lastNW = this.lastBounds.getNorthWest();
    const lastSE = this.lastBounds.getSouthEast();

    return (
      Math.abs(newNW.lat - lastNW.lat) > 0.1 ||
      Math.abs(newNW.lng - lastNW.lng) > 0.2 ||
      Math.abs(newSE.lat - lastSE.lat) > 0.1 ||
      Math.abs(newSE.lng - lastSE.lng) > 0.2
    );
  }
}
